import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import SimplePageHero from '../marketing/components/SimplePageHero';
import FaqAccordion from '../marketing/components/FaqAccordion';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';

const faqs = [
  { q: 'What is MillOS?', a: 'MillOS is an AI-powered compliance and accounting platform for UK landlords, letting agents and property investors. It tracks every certificate, chases every renewal and reconciles rent and expenses across your whole portfolio.' },
  { q: 'Which certificates does MillOS track?', a: 'Gas Safety (CP12), EPC, EICR, PAT, Fire Risk Assessment, Legionella, deposit protection and Right to Rent — plus HMO licences and any custom document you upload.' },
  { q: 'How does the AI read my certificates?', a: 'Upload a PDF or photo and MillOS extracts the expiry date, engineer details and property, then files it against the right unit and schedules the renewal workflow up to 90 days ahead.' },
  { q: 'Is my bank data safe?', a: 'Yes. MillOS uses FCA-regulated, read-only Open Banking. Credentials are tokenised, we never see your login, and MillOS can never move money — only read transactions you authorise.' },
  { q: 'Which banks are supported?', a: 'Every major UK bank plus Monzo, Starling, Metro and Revolut Business, via our regulated Open Banking provider.' },
  { q: 'Does it work for HMOs and large portfolios?', a: 'Yes. MillOS is built to scale from a single buy-to-let to 500+ units, with HMO-specific certificates, licence tracking, multi-user roles and bulk CSV import.' },
  { q: 'Can my accountant get access?', a: 'Yes — you can grant free, role-scoped access with a full audit log, so year-end becomes a non-event.' },
  { q: 'How much does it cost?', a: 'MillOS is free for a single property. Paid plans start at £12 per property per month, with custom Agency pricing for 50+ units. See the pricing page for details.' },
];

const HelpPage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'Help & FAQ | MillOS',
    description: 'Answers to common questions about MillOS — compliance tracking, Open Banking, pricing, security and getting started for UK landlords.',
    canonicalPath: '/help',
  });

  return (
    <Box className="lv-marketing" sx={{ background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'hidden' }}>
      <MarketingNav />
      <SimplePageHero
        eyebrow="Help centre"
        title={<>How can we <Box component="em" sx={{ fontStyle: 'italic' }}>help?</Box></>}
        body="The most common questions about MillOS, compliance and getting set up."
        maxWidth={820}
      />
      <Box component="section" sx={{ padding: { xs: '32px 16px 64px', md: '48px 52px 96px' } }}>
        <Box sx={{ maxWidth: 820, margin: '0 auto' }}>
          <FaqAccordion faqs={faqs} />
        </Box>
        <Box sx={{ maxWidth: 820, margin: '40px auto 0', background: C.tintBlue, borderRadius: '24px', padding: { xs: '32px 24px', md: '44px 40px' }, textAlign: 'center' }}>
          <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(1.7rem,3.2vw,2.4rem)', letterSpacing: '-0.02em' }}>
            Still stuck?
          </Box>
          <Box sx={{ maxWidth: '30rem', margin: '12px auto 0', color: C.body, fontSize: 16 }}>
            Our UK-based support team replies within one working hour on weekdays.
          </Box>
          <Box
            component={RouterLink}
            to="/register"
            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: C.inkSoft, color: C.white, borderRadius: '32px', padding: '13px 26px', fontSize: 16, marginTop: '22px', '&:hover': { background: C.accent } }}
          >
            Contact support
          </Box>
        </Box>
      </Box>
      <MarketingFooter />
    </Box>
  );
};

export default HelpPage;
